import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Download, Layers, ShieldAlert, X,
} from 'lucide-react';
import { Badge, Card, CardHeader, CardTitle, CountrySelect, OperatorSelect, PageHeader, ProductIntro } from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import type { ResponseEnvelope } from '@/types/api';
import { cn } from '@/lib/utils/cn';

// ── Types ─────────────────────────────────────────────────────────────────────

interface RiskEvent {
  transactionId: string;
  productCode: string;
  metadataId?: string;
  sessionId?: string;
  mobileNumberMasked?: string;
  operator?: string;
  countryCode?: string;
  riskScore: number;
  trustScore: number;
  riskLevel: string;
  decision: string;
  reasonCodes: string[];
  signals?: Record<string, unknown>;
  devicePlatform?: string;
  deviceRooted?: boolean;
  emulatorDetected?: boolean;
  vpnDetected?: boolean;
  countryMismatch?: boolean;
  createdAt: string;
}

interface PageDto<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface Filters {
  productCode: string;
  riskLevel: string;
  decision: string;
  countryCode: string;
  operator: string;
  from: string;
  to: string;
}

// ── API ───────────────────────────────────────────────────────────────────────

async function fetchRiskEvents(filters: Filters, page: number): Promise<PageDto<RiskEvent>> {
  const params: Record<string, string | number> = { page, size: 20 };
  if (filters.productCode) params.productCode = filters.productCode;
  if (filters.riskLevel) params.riskLevel = filters.riskLevel;
  if (filters.decision) params.decision = filters.decision;
  if (filters.countryCode) params.countryCode = filters.countryCode;
  if (filters.operator) params.operator = filters.operator;
  if (filters.from) params.from = filters.from;
  if (filters.to) params.to = filters.to;

  const res = await api.get<ResponseEnvelope<PageDto<RiskEvent>>>(Endpoints.me.riskEvents, { params });
  return unwrap(res);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function riskLevelStyle(level: string) {
  switch (level) {
    case 'LOW': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300';
    case 'MEDIUM': return 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300';
    case 'HIGH': return 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300';
    case 'CRITICAL': return 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300';
    default: return 'bg-surface-subtle text-fg-muted';
  }
}

function decisionTone(d: string) {
  switch (d) {
    case 'ALLOW': return 'success' as const;
    case 'CHALLENGE': return 'warning' as const;
    case 'REVIEW': return 'neutral' as const;
    case 'BLOCK': return 'danger' as const;
    default: return 'neutral' as const;
  }
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function downloadCsv(events: RiskEvent[]) {
  const header = ['Transaction ID', 'Product', 'Mobile', 'Country', 'Operator', 'Risk Score', 'Trust Score', 'Risk Level', 'Decision', 'Reason Codes', 'Time'];
  const rows = events.map((e) => [
    e.transactionId, e.productCode, e.mobileNumberMasked ?? '', e.countryCode ?? '', e.operator ?? '',
    e.riskScore, e.trustScore, e.riskLevel, e.decision, (e.reasonCodes ?? []).join('; '),
    new Date(e.createdAt).toISOString(),
  ]);
  const csv = [header, ...rows].map((r) => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  a.download = `risk-events-${Date.now()}.csv`;
  a.click();
}

// ── Detail drawer ─────────────────────────────────────────────────────────────

function RiskEventDrawer({ event, onClose }: { event: RiskEvent; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-md overflow-y-auto bg-surface shadow-2xl animate-in slide-in-from-right flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-fg">Risk Event Detail</p>
            <p className="text-xs font-mono text-fg-muted">{event.transactionId}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-surface-subtle transition-colors">
            <X className="h-4 w-4 text-fg-muted" />
          </button>
        </div>

        <div className="flex-1 space-y-5 p-5">
          {/* Decision + Risk */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-surface-muted/40 p-3 text-center">
              <p className="text-xs text-fg-muted mb-1">Decision</p>
              <Badge tone={decisionTone(event.decision)} className="text-sm font-bold">{event.decision}</Badge>
            </div>
            <div className="rounded-xl border border-border bg-surface-muted/40 p-3 text-center">
              <p className="text-xs text-fg-muted mb-1">Risk Level</p>
              <span className={cn('inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold', riskLevelStyle(event.riskLevel))}>
                {event.riskLevel}
              </span>
            </div>
          </div>

          {/* Scores */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-border bg-surface-muted/40 p-3 text-center">
              <p className="text-xs text-fg-muted mb-1">Risk Score</p>
              <p className="text-2xl font-bold text-fg">{event.riskScore}</p>
            </div>
            <div className="rounded-xl border border-border bg-surface-muted/40 p-3 text-center">
              <p className="text-xs text-fg-muted mb-1">Trust Score</p>
              <p className="text-2xl font-bold text-fg">{event.trustScore}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">Details</p>
            {[
              { label: 'Product', value: event.productCode },
              { label: 'Mobile', value: event.mobileNumberMasked },
              { label: 'Country', value: event.countryCode },
              { label: 'Operator', value: event.operator },
              { label: 'Device Platform', value: event.devicePlatform },
              { label: 'Time', value: fmt(event.createdAt) },
            ].filter((r) => r.value).map(({ label, value }) => (
              <div key={label} className="flex justify-between text-xs">
                <span className="text-fg-muted">{label}</span>
                <span className="font-medium text-fg">{value}</span>
              </div>
            ))}
          </div>

          {/* Flags */}
          {(event.deviceRooted != null || event.emulatorDetected != null || event.vpnDetected != null || event.countryMismatch != null) && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">Risk Signals</p>
              <div className="flex flex-wrap gap-2">
                {event.deviceRooted && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Rooted Device</span>}
                {event.emulatorDetected && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">Emulator</span>}
                {event.vpnDetected && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">VPN</span>}
                {event.countryMismatch && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Country Mismatch</span>}
              </div>
            </div>
          )}

          {/* Reason codes */}
          {event.reasonCodes?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">Reason Codes</p>
              <div className="flex flex-wrap gap-1.5">
                {event.reasonCodes.map((c) => (
                  <span key={c} className="rounded-md bg-surface-subtle px-2 py-0.5 font-mono text-[11px] text-fg-muted">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Detect anomalies */}
          {event.productCode === 'DETECT' && Array.isArray(event.signals?.['anomalyCodes']) && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-fg-muted uppercase tracking-wide">Detected Anomalies</p>
              <div className="flex flex-wrap gap-1.5">
                {(event.signals['anomalyCodes'] as string[]).map((code) => (
                  <span key={code} className="rounded-md bg-rose-50 px-2 py-0.5 font-mono text-[11px] text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
                    {code}
                  </span>
                ))}
              </div>
              {/* Window stats */}
              {Object.entries(event.signals).filter(([k]) => k !== 'anomalyCodes').length > 0 && (
                <div className="space-y-1 pt-1 border-t border-border">
                  <p className="text-[11px] font-semibold text-fg-muted uppercase tracking-wide">Window Stats</p>
                  {Object.entries(event.signals)
                    .filter(([k]) => k !== 'anomalyCodes')
                    .map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-fg-muted">{k.replace(/([A-Z])/g, ' $1').trim()}</span>
                        <span className="font-medium text-fg">{String(v)}</span>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}

          {/* What does this mean — plain language */}
          <div className="rounded-xl border border-border bg-surface-muted/40 p-4 text-xs text-fg-muted space-y-1">
            <p className="font-semibold text-fg">What this means</p>
            {event.decision === 'ALLOW' && <p>This identity cleared all risk checks. No action is needed.</p>}
            {event.decision === 'CHALLENGE' && <p>Moderate risk signals detected. Consider adding an extra verification step for this user.</p>}
            {event.decision === 'REVIEW' && <p>High risk detected. Manual review or additional identity confirmation is recommended before proceeding.</p>}
            {event.decision === 'BLOCK' && <p>Critical fraud risk detected. This request has been flagged and should be denied or escalated for investigation.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const EMPTY_FILTERS: Filters = {
  productCode: '', riskLevel: '', decision: '', countryCode: '', operator: '', from: '', to: '',
};

export function ClientRiskEventsPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<RiskEvent | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['me', 'risk-events', applied, page],
    queryFn: () => fetchRiskEvents(applied, page),
    staleTime: 30_000,
  });

  function applyFilters() { setApplied(filters); setPage(0); }
  function resetFilters() { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0); }

  const set = (k: keyof Filters) => (v: string) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Fraud Logs"
        subtitle="Every Shield, Trust, and Detect assessment for your account — one unified log."
        badge={<Badge tone="neutral">{data?.totalElements.toLocaleString() ?? 0} total</Badge>}
        actions={data?.content.length ? (
          <button
            onClick={() => downloadCsv(data.content)}
            className="flex items-center gap-1.5 rounded-xl border border-border bg-surface px-3 py-2 text-xs font-medium text-fg hover:bg-surface-subtle transition-colors shadow-card"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        ) : undefined}
      />

      <ProductIntro
        icon={Layers}
        tone="neutral"
        eyebrow="Customer Intelligence · Unified risk log"
        title="One searchable log for every risk decision across your account."
        points={['Shield', 'Trust', 'Detect', 'Filter & export']}
      >
        <p>
          This is the complete event trail behind your <strong>Customer Intelligence</strong> products — every
          <strong> Shield</strong> assessment, <strong>Trust</strong> score, and <strong>Detect</strong> anomaly
          analysis, in one place. Use the product filter to focus on a single engine, or leave it on
          <em> All Products</em> to see the full picture.
        </p>
        <p>
          Filter by risk level, decision, operator, country, or time range, click any row for the full reason
          codes and signals, and export the filtered view to CSV for your fraud-ops or audit workflows.
        </p>
      </ProductIntro>

      {/* Filters */}
      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <select
            value={filters.productCode}
            onChange={(e) => set('productCode')(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          >
            <option value="">All Products</option>
            <option value="SHIELD">Shield</option>
            <option value="DETECT">Detect</option>
            <option value="TRUST">Trust</option>
          </select>

          <select
            value={filters.riskLevel}
            onChange={(e) => set('riskLevel')(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          >
            <option value="">All Risk Levels</option>
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
            <option value="CRITICAL">Critical</option>
          </select>

          <select
            value={filters.decision}
            onChange={(e) => set('decision')(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          >
            <option value="">All Decisions</option>
            <option value="ALLOW">Allow</option>
            <option value="CHALLENGE">Challenge</option>
            <option value="REVIEW">Review</option>
            <option value="BLOCK">Block</option>
          </select>

          <CountrySelect value={filters.countryCode} onChange={set('countryCode')} />
        </div>

        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <OperatorSelect countryCode={filters.countryCode} value={filters.operator} onChange={set('operator')} />
          <input
            type="datetime-local"
            value={filters.from}
            onChange={(e) => set('from')(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
          <input
            type="datetime-local"
            value={filters.to}
            onChange={(e) => set('to')(e.target.value)}
            className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={applyFilters}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 transition-colors"
          >
            Apply
          </button>
          <button
            onClick={resetFilters}
            className="rounded-lg border border-border px-4 py-2 text-sm text-fg hover:bg-surface-subtle transition-colors"
          >
            Reset
          </button>
        </div>

        {/* Active filter chips */}
        {Object.entries(applied).some(([, v]) => Boolean(v)) && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
            <span className="text-[11px] text-fg-muted self-center">Active:</span>
            {Object.entries(applied)
              .filter(([, v]) => Boolean(v))
              .map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => {
                    const next = { ...applied, [k]: '' };
                    setFilters(next);
                    setApplied(next);
                    setPage(0);
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[11px] font-medium text-primary-700 hover:bg-primary-100 dark:border-primary-500/30 dark:bg-primary-500/10 dark:text-primary-300 dark:hover:bg-primary-500/20 transition-colors"
                >
                  {k}: {String(v)}
                  <X className="h-2.5 w-2.5" aria-hidden />
                </button>
              ))}
          </div>
        )}
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            {data ? `${data.totalElements.toLocaleString()} Events` : 'Risk Events'}
          </CardTitle>
        </CardHeader>
        {isLoading ? (
          <div className="space-y-0">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-0">
                <div className="h-3 w-32 animate-pulse rounded bg-surface-muted" />
                <div className="h-3 w-16 animate-pulse rounded bg-surface-muted" />
                <div className="ml-auto h-3 w-14 animate-pulse rounded bg-surface-muted" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="flex items-center gap-3 px-5 py-8">
            <ShieldAlert className="h-5 w-5 text-rose-400" />
            <p className="text-sm text-rose-600 dark:text-rose-400">Failed to load events. Try refreshing the page.</p>
          </div>
        ) : !data?.content.length ? (
          <div className="py-16 text-center">
            <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-fg-muted/30" />
            <p className="text-sm font-medium text-fg">No events found</p>
            <p className="mt-1 text-xs text-fg-muted">Adjust filters or check back after running assessments.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Transaction</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Product</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Mobile</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Risk Level</th>
                    <th className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Risk Score</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Decision</th>
                    <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-fg-muted">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {data.content.map((e) => {
                    const scoreColor =
                      e.riskScore >= 75 ? '#ef4444' :
                      e.riskScore >= 50 ? '#f97316' :
                      e.riskScore >= 25 ? '#f59e0b' : '#10b981';
                    return (
                      <tr
                        key={e.transactionId}
                        className="cursor-pointer border-b border-border last:border-0 hover:bg-surface-muted/40 transition-colors group"
                        onClick={() => setSelected(e)}
                      >
                        <td className="px-4 py-3">
                          <p className="text-xs font-mono text-fg group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                            {e.transactionId.slice(0, 20)}&hellip;
                          </p>
                          {e.metadataId && (
                            <p className="text-[10px] text-fg-subtle">{e.metadataId}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-fg">{e.productCode}</td>
                        <td className="px-4 py-3 text-xs text-fg-muted font-mono">{e.mobileNumberMasked || '—'}</td>
                        <td className="px-4 py-3">
                          <span className={cn('inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold', riskLevelStyle(e.riskLevel))}>
                            {e.riskLevel}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {e.productCode === 'DETECT' ? (
                            <div className="flex items-center gap-1">
                              {((e.signals?.['anomalyCodes'] as string[] | undefined) ?? []).slice(0, 2).map((code) => (
                                <span key={code} className="rounded bg-rose-50 px-1.5 py-0.5 font-mono text-[9px] text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 truncate max-w-[80px]">
                                  {code}
                                </span>
                              ))}
                              {((e.signals?.['anomalyCodes'] as string[] | undefined) ?? []).length > 2 && (
                                <span className="text-[10px] text-fg-muted">+{((e.signals?.['anomalyCodes'] as string[]).length - 2)}</span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="w-6 shrink-0 text-right text-xs font-bold tabular-nums" style={{ color: scoreColor }}>
                                {e.riskScore}
                              </span>
                              <div className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-subtle">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{ width: `${e.riskScore}%`, background: scoreColor }}
                                />
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Badge tone={decisionTone(e.decision)}>{e.decision}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-fg-muted whitespace-nowrap">{fmt(e.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border pt-4">
                <p className="text-xs text-fg-muted">
                  Page {data.number + 1} of {data.totalPages}
                </p>
                <div className="flex gap-1">
                  <button
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    className="rounded-lg border border-border p-1.5 text-fg-muted hover:bg-surface-subtle disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={page >= data.totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-lg border border-border p-1.5 text-fg-muted hover:bg-surface-subtle disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {selected && <RiskEventDrawer event={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
